import React, { useMemo } from "react";
import { useClinics } from "@/hooks/useClinics";
import { useProducts } from "@/components/product-step/useProducts";
import { useClinicProductAvailability } from "@/hooks/useClinicProductAvailability";
import { getTranslations as getClinicStepTranslations } from "./clinic-step/ClinicStepTranslations";
import { ClinicList } from "./clinic-step/ClinicList";
import { Header } from "./clinic-step/Header";
import { ExpandableOrderSummary } from "./booking/ExpandableOrderSummary";
import { Product } from "./product-step/types";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { useMarket } from "@/hooks/useMarket";
import { t as translate } from "@/locales";
import { useMembership } from "@/contexts/MembershipContext";

// This component now uses the bookingFlowStore directly instead of receiving props
// No props needed - everything comes from the store!
const ClinicStep = () => {
  // Get all state and actions from the booking flow store
  const {
    language,
    selectedPackageType,
    selectedClinic,
    selectClinic,
    selectedProducts,
    selectedAddOns,
    removeProduct,
    toggleAddOn,
    goToStep,
    goBack,
    canGoBack,
    canProceed,
    selectedTimeSlots,
    selectedProductDetails,
    selectedAddOnDetails,
  } = useBookingFlow();

  const { market } = useMarket();
  const { data: clinics, isLoading: clinicsLoading } = useClinics();
  const { data: productsData, isLoading: productsLoading } = useProducts(
    language,
    market,
  );
  const { data: availabilityData, isLoading: availabilityLoading } =
    useClinicProductAvailability();
  const { getEffectivePrice } = useMembership();

  const t = getClinicStepTranslations(language);

  // Filter clinics by market (country) first, then by selected package type.
  const filteredClinics = useMemo(() => {
    if (!clinics) return [];
    return clinics.filter((clinic) => {
      if ((clinic.country || "ch") !== market) return false;
      const acceptedTypes = clinic.package_types || ["mri", "blood-only"];
      return acceptedTypes.includes(selectedPackageType);
    });
  }, [clinics, market, selectedPackageType]);

  // Combine selected products and add-ons for clinic availability checking
  // Filter out any undefined/null values to prevent errors
  const allSelectedProducts = useMemo(
    () => [
      ...(selectedProducts || []).filter(Boolean),
      ...(selectedAddOns || []).filter(Boolean),
    ],
    [selectedProducts, selectedAddOns],
  );

  // One clinic means there is nothing to choose — preselect it so the Continue
  // button is live on arrival (AHD-8731). A market change resets the flow (the
  // only persisted state is sessionId/language/market, and a ?country switch
  // drops the sessionId), so there's no stale selection to guard against.
  React.useEffect(() => {
    if (selectedClinic || filteredClinics.length !== 1) return;
    selectClinic(filteredClinics[0].id);
  }, [selectedClinic, filteredClinics, selectClinic]);

  // Just update the selected clinic (store handles navigation via continue button)
  const handleClinicSelect = (clinicId: string) => {
    console.log("[ClinicStep] Selecting clinic:", clinicId);
    // Note: selectClinic in store auto-navigates to date step
    // But we keep the continue button for better UX control
    selectClinic(clinicId);
  };

  // Enhanced product removal handler that can handle both main products and add-ons
  const handleProductRemove = (productId: string) => {
    console.log("[ClinicStep] Removing product:", productId);

    // Check if it's in selectedProducts first
    if (selectedProducts.includes(productId)) {
      removeProduct(productId);
    }
    // If not in selectedProducts, check if it's in selectedAddOns
    else if (selectedAddOns.includes(productId)) {
      toggleAddOn(productId);
    }
  };

  const handleContinue = () => {
    console.log("[ClinicStep] Continue button clicked");
    // Store's goToStep handles validation automatically
    goToStep("date");
  };

  if (clinicsLoading || productsLoading || availabilityLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  console.log("[ClinicStep] Rendering with:", {
    selectedClinic,
    selectedProducts,
    selectedAddOns,
    canProceed,
  });

  // Get selected clinic object
  const selectedClinicObject = clinics?.find((c) => c.id === selectedClinic);

  // Calculate total price using member-effective prices so the collapsed
  // summary bar matches the per-item discounted prices shown above.
  const totalPrice = [
    ...selectedProductDetails,
    ...selectedAddOnDetails,
  ].reduce((sum, product) => sum + getEffectivePrice(product), 0);

  // Get centralized translations for order summary
  const orderSummaryTranslations = {
    orderSummary: translate(language, "orderSummary.title"),
    scan: translate(language, "orderSummary.scan"),
    addOns: translate(language, "orderSummary.addOns"),
    bloodTests: translate(language, "orderSummary.bloodTests"),
    location: translate(language, "orderSummary.location"),
    timeSlots: translate(language, "orderSummary.timeSlots"),
    totalPrice: translate(language, "orderSummary.totalPrice"),
    continue: translate(language, "common.next"),
    back: translate(language, "common.prev"),
    remove: translate(language, "common.remove"),
  };

  return (
    <>
      <div className="pb-12">
        <Header language={language} />

        <ClinicList
          clinics={filteredClinics}
          market={market}
          selectedClinic={selectedClinic}
          onClinicChange={handleClinicSelect}
          selectedProducts={allSelectedProducts}
          availabilityData={
            (availabilityData || []) as unknown as Record<string, unknown>[]
          }
          productsData={
            (productsData?.products || []) as unknown as Record<
              string,
              unknown
            >[]
          }
          onProductRemove={handleProductRemove}
          translations={{
            notAvailable: t.notAvailable,
            removeProduct: t.removeProduct,
            makeAvailable: t.makeAvailable,
            unavailableSuffix: t.unavailableSuffix,
            maps: t.maps,
            regionZhKanton: t.regionZhKanton,
            regionZentralschweiz: t.regionZentralschweiz,
            regionNordwestschweiz: t.regionNordwestschweiz,
            regionMittelland: t.regionMittelland,
            regionOstschweiz: t.regionOstschweiz,
            regionWestschweiz: t.regionWestschweiz,
            regionOther: t.regionOther,
          }}
        />
      </div>

      {/* Expandable Order Summary Footer */}
      <ExpandableOrderSummary
        totalPrice={totalPrice}
        selectedProducts={
          selectedProductDetails as Parameters<
            typeof ExpandableOrderSummary
          >[0]["selectedProducts"]
        }
        selectedAddOns={
          selectedAddOnDetails as Parameters<
            typeof ExpandableOrderSummary
          >[0]["selectedAddOns"]
        }
        selectedClinic={selectedClinicObject}
        selectedTimeSlots={selectedTimeSlots}
        language={language}
        onContinue={canProceed ? handleContinue : undefined}
        onBack={goBack}
        showBackButton={true}
        showContinueButton={true}
        continueDisabled={!canProceed}
        backDisabled={!canGoBack}
        onProductRemove={removeProduct}
        onAddOnRemove={toggleAddOn}
        onEditProducts={() => goToStep("product", { skipValidation: true })}
        onEditAddOns={() => goToStep("addons", { skipValidation: true })}
        onEditTimeSlots={() => goToStep("date", { skipValidation: true })}
        translations={orderSummaryTranslations}
        currentStep="clinic"
      />
    </>
  );
};

export default ClinicStep;
